import { useCallback, useEffect, useRef } from 'react';
import { useFormStore } from '../stores/formStore';
import { useUser, useApi } from 'react-ui';
import { getDraftService, DraftSaveStatus } from '../services/draftService';
import { EventFormData } from '../types';

const DEBOUNCE_DELAY = 2000; // 2 seconds
const MIN_TITLE_LENGTH = 3;

interface UseDraftAutoSaveOptions {
  enabled?: boolean;
  onSaveSuccess?: (draftId: string) => void;
  onSaveError?: (error: Error) => void;
}

interface UseDraftAutoSaveReturn {
  saveStatus: DraftSaveStatus;
  lastSaved: Date | null;
  triggerSave: (immediate?: boolean) => void;
  deleteDraft: () => Promise<void>;
}

/**
 * Hook for auto-saving drafts to the API
 * 
 * - Creates draft when title has ≥3 characters
 * - Updates draft on field changes with 2s debounce
 * - Immediate save on step navigation
 */
export function useDraftAutoSave(options: UseDraftAutoSaveOptions = {}): UseDraftAutoSaveReturn {
  const { enabled = true, onSaveSuccess, onSaveError } = options;
  
  const { 
    formData, 
    draftId, 
    isDirty,
    saveStatus,
    lastSaved,
    setDraftId,
    setSaveStatus,
    setLastSaved,
    markClean,
  } = useFormStore();
  
  const { user } = useUser();
  const { apiEndpoints, apiKey } = useApi();
  
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef(false);
  const previousFormDataRef = useRef<EventFormData | null>(null);
  const formDataRef = useRef(formData);
  const draftIdRef = useRef(draftId);

  // Keep refs in sync with latest values
  formDataRef.current = formData;
  draftIdRef.current = draftId;

  // Get token from user object
  const token = user?.token;

  // Get API base URL from endpoints
  const apiEndpoint = apiEndpoints?.metadata 
    ? new URL(apiEndpoints.metadata).origin 
    : '';

  /**
   * Perform the actual save operation
   */
  const performSave = useCallback(async () => {
    // Read latest values from refs to avoid dependency on formData/draftId
    const currentFormData = formDataRef.current;
    const currentDraftId = draftIdRef.current;

    // Guards
    if (!enabled || !user || !token || !apiEndpoint || !apiKey) return;
    if (isSavingRef.current) return;
    if (currentFormData.title.length < MIN_TITLE_LENGTH) return;

    isSavingRef.current = true;
    setSaveStatus('saving');

    try {
      const service = getDraftService(apiEndpoint, apiKey);
      
      let result;
      if (currentDraftId) {
        console.log('[DraftAutoSave] Updating draft:', currentDraftId);
        result = await service.updateDraft(token, currentDraftId, currentFormData);
      } else {
        console.log('[DraftAutoSave] Creating new draft');
        result = await service.createDraft(token, currentFormData);
        if (result.draftId) {
          setDraftId(result.draftId);
        }
      }

      setSaveStatus('saved');
      setLastSaved(new Date());
      previousFormDataRef.current = { ...formDataRef.current };
      markClean();
      
      if (result.draftId) {
        onSaveSuccess?.(result.draftId);
      }
      
      console.log('[DraftAutoSave] Save successful');
    } catch (error) {
      console.error('[DraftAutoSave] Save failed:', error);
      setSaveStatus('error');
      onSaveError?.(error instanceof Error ? error : new Error(String(error)));
    } finally {
      isSavingRef.current = false;
    }
  }, [enabled, user, token, apiEndpoint, apiKey, setDraftId, setSaveStatus, setLastSaved, markClean, onSaveSuccess, onSaveError]);

  /**
   * Trigger a save (debounced by default, immediate if specified)
   */
  const triggerSave = useCallback((immediate = false) => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    if (immediate) {
      performSave();
    } else {
      debounceTimeoutRef.current = setTimeout(() => {
        performSave();
      }, DEBOUNCE_DELAY);
    }
  }, [performSave]);

  /**
   * Delete the current draft
   */
  const deleteDraft = useCallback(async () => {
    if (!draftId || !token || !apiEndpoint || !apiKey) return;

    try {
      const service = getDraftService(apiEndpoint, apiKey);
      await service.deleteDraft(token, draftId);
      setDraftId(null);
      console.log('[DraftAutoSave] Draft deleted:', draftId);
    } catch (error) {
      console.error('[DraftAutoSave] Failed to delete draft:', error);
      throw error;
    }
  }, [draftId, token, apiEndpoint, apiKey, setDraftId]);

  /**
   * Watch for form changes and trigger debounced save
   */
  useEffect(() => {
    if (!enabled || !isDirty) return;

    // Check if form data actually changed (beyond just isDirty flag)
    const hasRealChanges = previousFormDataRef.current === null || 
      JSON.stringify(formData) !== JSON.stringify(previousFormDataRef.current);

    if (hasRealChanges && formData.title.length >= MIN_TITLE_LENGTH) {
      triggerSave(false);
    }
  }, [enabled, isDirty, formData, triggerSave]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    saveStatus,
    lastSaved,
    triggerSave,
    deleteDraft,
  };
}
